/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, Edit3, Clock, Sparkles, Loader2, Package, VideoIcon, Copy } from 'lucide-react';
import {
  listServiceTemplates, listMyServices, createService, updateService, deleteService,
} from '@/lib/sprint4';

const CATEGORIES = [
  { key: 'visita', label: 'Visita' },
  { key: 'consulto_asincrono', label: 'Consulto asincrono' },
  { key: 'certificato', label: 'Certificato' },
  { key: 'prescrizione', label: 'Prescrizione' },
  { key: 'wound_care', label: 'Wound care' },
  { key: 'educazione_terapeutica', label: 'Educazione terapeutica' },
  { key: 'monitoraggio_iot', label: 'Monitoraggio IoT' },
  { key: 'consulenza_allattamento', label: 'Consulenza allattamento' },
  { key: 'videochiamata_come_fare', label: '“Come fare” in videochiamata' },
  { key: 'vaccinale', label: 'Consulenza vaccinale' },
  { key: 'compagnia_anziani', label: 'Compagnia anziani' },
  { key: 'formazione_caregiver', label: 'Formazione caregiver' },
  { key: 'piano_adl', label: 'Piano ADL' },
  { key: 'altro', label: 'Altro' },
];

const empty = () => ({
  title: '', description: '', category: 'visita',
  duration_minutes: 30, price_eur: 40, telemedicine: true, active: true,
});

const ProfessionalServicesPage = () => {
  const [services, setServices] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [role, setRole] = useState('medico');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [tpl, mine] = await Promise.all([listServiceTemplates(), listMyServices()]);
      setTemplates(Array.isArray(tpl.templates) ? tpl.templates : []);
      setRole(tpl.role || 'medico');
      setServices(mine.services || []);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error(e?.response?.data?.detail || 'Errore caricamento');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = (seed = null) => {
    setEditingId(null);
    setForm(seed ? {
      ...empty(), ...seed,
      title: seed.title || '',
      duration_minutes: seed.duration_minutes || 30,
      price_eur: seed.price_eur || 40,
    } : empty());
    setDialogOpen(true);
  };
  const openEdit = (s) => { setEditingId(s.id); setForm({ ...s }); setDialogOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        const updated = await updateService(editingId, form);
        setServices((cur) => (cur || []).map((s) => (s.id === updated.id ? updated : s)));
        toast.success('Servizio aggiornato');
      } else {
        const created = await createService(form);
        setServices((cur) => [created, ...(cur || [])]);
        toast.success('Servizio aggiunto al catalogo');
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const [acting, setActing] = useState(false);
  const remove = async (s) => {
    if (acting || !window.confirm(`Rimuovere “${s.title}” dal catalogo?`)) return;
    setActing(true);
    try {
      await deleteService(s.id);
      setServices((cur) => (cur || []).filter((x) => x.id !== s.id));
      toast.success('Servizio rimosso');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    } finally { setActing(false); }
  };

  const roleLabel = { medico: 'Medico', infermiere: 'Infermiere', oss: 'OSS' }[role] || role;

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="services-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Package className="h-6 w-6 text-primary" /> Le mie prestazioni
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Definisci il tuo catalogo di servizi in telemedicina. I pazienti sceglieranno la prestazione prima di prenotare, così sai subito cosa aspettarti.
          </p>
          {role && <Badge variant="outline" className="mt-2">Profilo: {roleLabel}</Badge>}
        </div>
        <Button onClick={() => openCreate()} data-testid="service-new-btn">
          <Plus className="h-4 w-4" /> Nuova prestazione
        </Button>
      </div>

      {/* Templates */}
      {templates.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-display font-semibold text-lg">Template consigliati per il tuo ruolo</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map((t, i) => (
              <Card key={`tpl-${i}-${t.title.slice(0,10)}`} className="hover:border-primary/40 transition-colors" data-testid={`service-template-${i}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-sm">{t.title}</div>
                    <Badge variant="outline" className="text-[10px]">€{t.price_eur}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground min-h-[36px] leading-relaxed">{t.description}</p>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <Clock className="h-3 w-3" /> {t.duration_minutes} min
                    {t.telemedicine && <><VideoIcon className="h-3 w-3 ml-1" /> Online</>}
                  </div>
                  <Button size="sm" variant="outline" className="w-full" onClick={() => openCreate(t)} data-testid={`service-tpl-use-${i}`}>
                    <Copy className="h-3.5 w-3.5" /> Usa come base
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Own services */}
      <section className="space-y-3">
        <h2 className="font-display font-semibold text-lg">Prestazioni attive</h2>
        {!services ? (
          <div className="grid gap-3 sm:grid-cols-2"><Skeleton className="h-28" /><Skeleton className="h-28" /></div>
        ) : services.length === 0 ? (
          <Card><CardContent className="p-8 text-center space-y-3">
            <Sparkles className="h-10 w-10 text-primary mx-auto" />
            <div className="text-sm">Non hai ancora prestazioni. Inizia da un template qui sopra o crea la tua.</div>
            <Button onClick={() => openCreate()} data-testid="services-empty-cta"><Plus className="h-4 w-4" /> Nuova prestazione</Button>
          </CardContent></Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => (
              <Card key={s.id} className={!s.active ? 'opacity-60' : ''} data-testid={`service-row-${s.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-sm">{s.title}</CardTitle>
                    <div className="flex items-center gap-1">
                      <Badge variant="outline" className="text-[10px]">€{s.price_eur}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-xs text-muted-foreground min-h-[36px] leading-relaxed">{s.description || '—'}</p>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <Clock className="h-3 w-3" /> {s.duration_minutes} min
                    {s.telemedicine && <><VideoIcon className="h-3 w-3 ml-1" /> Online</>}
                    <span className="ml-auto">{CATEGORIES.find((c) => c.key === s.category)?.label || s.category}</span>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(s)} data-testid={`service-edit-${s.id}`}>
                      <Edit3 className="h-3.5 w-3.5" /> Modifica
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(s)} disabled={acting} aria-label="Rimuovi servizio" data-testid={`service-delete-${s.id}`} className="text-destructive hover:text-destructive">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Editor dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="service-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica prestazione' : 'Nuova prestazione'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3" data-testid="service-editor-form">
            <div>
              <Label>Titolo *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="service-title-input" />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} maxLength={800} data-testid="service-desc-input" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="service-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Durata (min)</Label>
                <Input type="number" min={5} max={240} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} data-testid="service-duration-input" />
              </div>
              <div>
                <Label>Prezzo (€)</Label>
                <Input type="number" step="0.5" min={0} max={1000} value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: Number(e.target.value) })} data-testid="service-price-input" />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.telemedicine} onCheckedChange={(v) => setForm({ ...form, telemedicine: v })} data-testid="service-telemedicine-switch" /> Telemedicina
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="service-active-switch" /> Attiva
              </label>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={saving || !form.title.trim()} data-testid="service-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {editingId ? 'Aggiorna' : 'Aggiungi al catalogo'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfessionalServicesPage;
